import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { useContext, useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { queryClient } from "@/queryClient";
import useMarkAsRubbish from "@/modules/lead-page/hooks/lead-info/lead-actions/mark-as-rubbish.hook";
import ConfirmationModal from "@/modules/common/components/modals/cofirmation-modal";
import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import { INVALID_CUSTOMER_REASONS } from "@/modules/lead-page/constants";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

const MarkAsRubbish = () => {
  const { lead } = useContext(LeadPageContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [commentError, setCommentError] = useState("");
  const actionTypeRef = useRef<"mark" | "unmark" | null>(null);

  const { handleMarkAsRubbishJourney, isError, isSuccess, isJourneyLoading } =
    useMarkAsRubbish();

  const isRubbish = lead?.data?.lead?.rubbish || lead?.data?.rubbish;

  useEffect(() => {
    const hasError = Object.values(isError).some((value) => value === true);
    if (hasError) {
      toast.error("Something went wrong", {
        position: "bottom-left",
        autoClose: 3000,
      });
    }
  }, [isError]);

  useEffect(() => {
    if (!isSuccess || !actionTypeRef.current) return;

    const queryKeysToInvalidate = [["getLead"], ["getComments"]];
    queryKeysToInvalidate.forEach((key) =>
      queryClient.invalidateQueries({ queryKey: key })
    );

    const message =
      actionTypeRef.current === "unmark"
        ? "Marked as valid client successfully"
        : "Marked as invalid client successfully";

    toast.success(message, {
      position: "bottom-left",
      autoClose: 2000,
    });

    setModalOpen(false);
    setConfirmationModalOpen(false);
    actionTypeRef.current = null; // Reset the action type

    // Reset form fields
    setReason("");
    setComment("");
    setReasonError("");
    setCommentError("");
  }, [isSuccess]);

  const handleClick = () => {
    if (isRubbish) {
      actionTypeRef.current = "unmark";
      setConfirmationModalOpen(true);
    } else {
      actionTypeRef.current = "mark";
      setModalOpen(true);
    }
  };

  const handleConfirmUnmark = () => {
    handleMarkAsRubbishJourney();
  };

  const handleConfirmMark = () => {
    let hasError = false;

    if (!reason) {
      setReasonError("Reason is required");
      hasError = true;
    } else {
      setReasonError("");
    }

    if (!comment.trim()) {
      setCommentError("Comment is required");
      hasError = true;
    } else {
      setCommentError("");
    }

    if (!hasError) {
      handleMarkAsRubbishJourney(reason, comment);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setReason("");
    setComment("");
    setReasonError("");
    setCommentError("");
  };

  return (
    <>
      {/* ResponsiveEditDialog for marking as rubbish (with reason and comment) */}
      <ResponsiveEditDialog
        open={modalOpen}
        setOpen={setModalOpen}
        header="Invalid Client"
        handleConfirm={handleConfirmMark}
        handleCloseModal={handleCloseModal}
        confirmText="Invalid Client"
        cancelText="Cancel"
        isLoading={isJourneyLoading}
        modalFooter={true}
        disabledConfirmButton={!reason || !comment.trim()}
        width={500}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            p: 2,
          }}
        >
          <Typography variant="body1">
            Please provide a reason and comment for marking this journey as
            invalid client:
          </Typography>

          {/* Reason Dropdown */}
          <FormControl fullWidth error={!!reasonError}>
            <InputLabel>Reason *</InputLabel>
            <Select
              value={reason}
              label="Reason *"
              onChange={(e) => setReason(e.target.value)}
              disabled={isJourneyLoading}
            >
              {Object.values(INVALID_CUSTOMER_REASONS).map((reasonOption) => (
                <MenuItem key={reasonOption} value={reasonOption}>
                  {reasonOption}
                </MenuItem>
              ))}
            </Select>
            {reasonError && <FormHelperText>{reasonError}</FormHelperText>}
          </FormControl>

          {/* Comment Field */}
          <TextField
            fullWidth
            label="Comment *"
            multiline
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            error={!!commentError}
            helperText={commentError}
            disabled={isJourneyLoading}
            placeholder="Please provide additional details..."
          />
        </Box>
      </ResponsiveEditDialog>

      {/* Simple confirmation modal for un-marking */}
      <ConfirmationModal
        open={confirmationModalOpen}
        setOpen={setConfirmationModalOpen}
        title="Valid Customer"
        body="Are you sure you want to mark this journey as valid customer?"
        onConfirmHandler={handleConfirmUnmark}
        isLoading={isJourneyLoading}
      />

      <Box
        sx={{
          display: "flex",
          gap: nawyTheme.nawy.spacing.xs,
          padding: nawyTheme.nawy.spacing.xs,
          alignItems: "center",
          width: "100%",
          height: "100%",
          cursor: "pointer",
          ":hover": {
            backgroundColor: nawyTheme.nawy.colors.semantic.layer.secondary,
          },
        }}
        onClick={handleClick}
      >
        <Typography
          sx={{
            color:
              lead?.data?.lead?.rubbish || lead?.data?.rubbish
                ? nawyTheme.nawy.colors.semantic.text.extension.positive
                : nawyTheme.nawy.colors.semantic.text.extension.negative,
            ...nawyTheme.nawy.typography.english.variants.body.md.regular,
          }}
        >
          {lead?.data?.lead?.rubbish || lead?.data?.rubbish
            ? "Valid Client"
            : "Invalid Client"}
        </Typography>
      </Box>
    </>
  );
};

export default MarkAsRubbish;
